/**
 * Read-only renderer for a single business plan module.
 * Used in the public catalog detail view and the (future) read-only project view.
 *
 * Renders concept, canvas, market, operations, hr, calendar, marketing, budget,
 * risks and executive_summary using the same editorial styling as the rest of
 * the app, but with no editing affordances.
 */
import { useLang } from "@/contexts/LanguageContext";
import { Lock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

function H2({ children }) {
  return (
    <h2
      className="text-2xl sm:text-3xl tracking-tight text-[#1A1A1A]"
      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
    >
      {children}
    </h2>
  );
}
function H3({ children }) {
  return (
    <h3
      className="text-lg text-[#2563EB] mt-6 mb-3"
      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
    >
      {children}
    </h3>
  );
}
function P({ children }) {
  return (
    <p className="text-sm leading-relaxed text-[#1A1A1A] whitespace-pre-wrap">
      {children || "—"}
    </p>
  );
}
function KV({ k, v }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-3 py-2 border-b border-[#F0EFE9]">
      <div className="text-xs uppercase tracking-wide text-[#8A8886]">{k}</div>
      <div className="text-sm text-[#1A1A1A]">{v || "—"}</div>
    </div>
  );
}
function Overline({ children }) {
  return (
    <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A8886]">{children}</div>
  );
}
function Card({ children, accent = false }) {
  return (
    <div className={`bg-white border border-[#E8E6E1] rounded-sm p-6 ${accent ? "border-l-2 border-l-[#2563EB]" : ""}`}>
      {children}
    </div>
  );
}
function fmtEur(n) {
  if (n == null || isNaN(n)) return "€0";
  return `€${Math.round(n).toLocaleString("es-ES")}`;
}

export function LockedModuleCard({ moduleName }) {
  const { lang } = useLang();
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-sm p-12 text-center" data-testid="locked-module-card">
      <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F5EFE6] border border-[#B89154]/20 rounded-sm mb-5">
        <Lock className="h-6 w-6 text-[#B89154]" strokeWidth={1.5} />
      </div>
      <h3
        className="text-2xl text-[#1A1A1A]"
        style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
      >
        {lang === "es" ? "Módulo bloqueado" : "Locked module"}
      </h3>
      <p className="mt-3 text-sm text-[#5C5B5A] max-w-md mx-auto">
        {lang === "es"
          ? `El módulo "${moduleName}" sólo está disponible con suscripción Basic. Suscríbete para desbloquear los 12 planes completos del catálogo.`
          : `The "${moduleName}" module is only available with a Basic subscription. Subscribe to unlock all 12 complete catalog plans.`}
      </p>
      <div className="mt-6">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-[#2563EB] text-[#F9F8F6] hover:bg-[#1D4ED8] rounded-sm px-6 py-2.5 text-sm transition-all"
          data-testid="locked-module-pricing-btn"
        >
          {lang === "es" ? "Ver planes" : "See pricing"}
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}

export default function ReadOnlyModule({ moduleKey, data, project }) {
  const { t, lang } = useLang();
  const d = data || {};

  if (moduleKey === "executive_summary") {
    return (
      <Card accent>
        <Overline>{t("module.executive_summary")}</Overline>
        <H2>{t("exec.title")}</H2>
        <div className="mt-6">
          <P>{d.summary}</P>
        </div>
      </Card>
    );
  }

  if (moduleKey === "concept") {
    return (
      <Card>
        <Overline>{t("module.concept")}</Overline>
        <H2>{d.company_name || project?.name}</H2>
        <div className="mt-6 space-y-1">
          <KV k={t("concept.legal_form")} v={d.legal_form} />
          <KV k={t("concept.location")} v={d.location} />
        </div>
        <H3>{t("concept.idea")}</H3><P>{d.idea}</P>
        <H3>{t("concept.differentiators")}</H3><P>{d.differentiators}</P>
        <H3>{t("concept.competitive_advantage")}</H3><P>{d.competitive_advantage}</P>
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-5">
            <Overline>{t("concept.mission")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{d.mission || "—"}</p>
          </div>
          <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-5">
            <Overline>{t("concept.vision")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{d.vision || "—"}</p>
          </div>
          <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-5">
            <Overline>{t("concept.values")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{d.values || "—"}</p>
          </div>
        </div>
        {(d.partners || []).length > 0 && (
          <>
            <H3>{t("concept.partners")}</H3>
            <div className="space-y-2">
              {(d.partners || []).map((p, i) => (
                <div key={i} className="flex justify-between items-baseline gap-4 border-b border-[#F0EFE9] py-2">
                  <div className="text-sm text-[#1A1A1A]">{p.name} <span className="text-[#8A8886]">· {p.role}</span></div>
                  <div className="text-sm font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{p.equity ?? 0}%</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    );
  }

  if (moduleKey === "canvas") {
    const cells = [
      ["key_partners", "canvas.key_partners"],
      ["key_activities", "canvas.key_activities"],
      ["key_resources", "canvas.key_resources"],
      ["value_propositions", "canvas.value_propositions"],
      ["customer_relationships", "canvas.customer_relationships"],
      ["channels", "canvas.channels"],
      ["customer_segments", "canvas.customer_segments"],
      ["cost_structure", "canvas.cost_structure"],
      ["revenue_streams", "canvas.revenue_streams"],
    ];
    return (
      <Card>
        <Overline>{t("module.canvas")}</Overline>
        <H2>{t("module.canvas")}</H2>
        <p className="mt-2 text-sm text-[#5C5B5A]">{t("canvas.subtitle")}</p>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {cells.map(([k, label]) => (
            <div key={k} className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-5">
              <Overline>{t(label)}</Overline>
              <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{d[k] || "—"}</p>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (moduleKey === "market") {
    const sw = d.swot || {};
    return (
      <Card>
        <Overline>{t("module.market")}</Overline>
        <H2>{t("module.market")}</H2>
        <H3>{t("market.size")}</H3><P>{d.market_size}</P>
        <H3>{t("market.opportunity")}</H3><P>{d.opportunity}</P>
        <H3>{t("market.client_def")}</H3><P>{d.client_definition}</P>

        {(d.competitors || []).length > 0 && (
          <>
            <H3>{t("market.competitors")}</H3>
            <div className="space-y-2">
              {(d.competitors || []).map((c, i) => (
                <div key={i} className="border border-[#E8E6E1] rounded-sm p-4 bg-[#FBFAF7]">
                  <div className="text-sm font-medium text-[#1A1A1A]">{c.name}</div>
                  {c.description && <p className="text-xs text-[#5C5B5A] mt-1">{c.description}</p>}
                  <div className="mt-2 grid md:grid-cols-2 gap-2 text-xs">
                    <div><span className="text-[#8A8886]">{t("swot.strengths")}: </span>{c.strengths || "—"}</div>
                    <div><span className="text-[#8A8886]">{t("swot.weaknesses")}: </span>{c.weaknesses || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {(d.buyer_personas || []).length > 0 && (
          <>
            <H3>{t("market.personas")}</H3>
            <div className="grid md:grid-cols-2 gap-4">
              {(d.buyer_personas || []).map((p, i) => (
                <div key={i} className="border border-[#E8E6E1] rounded-sm p-5 bg-[#FBFAF7]">
                  <div className="text-base text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>{p.name}</div>
                  <Overline>{p.age} · {p.role}</Overline>
                  <div className="mt-3 space-y-2 text-xs">
                    {p.goals && <div><span className="text-[#8A8886]">Goals: </span>{p.goals}</div>}
                    {p.pains && <div><span className="text-[#8A8886]">Pains: </span>{p.pains}</div>}
                    {p.channels && <div><span className="text-[#8A8886]">Channels: </span>{p.channels}</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <H3>{t("market.swot")}</H3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-[#EFF4FF] border border-[#2563EB]/20 rounded-sm p-4">
            <Overline>{t("swot.strengths")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{sw.strengths || "—"}</p>
          </div>
          <div className="bg-[#FAEDED] border border-[#9E2A2B]/20 rounded-sm p-4">
            <Overline>{t("swot.weaknesses")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{sw.weaknesses || "—"}</p>
          </div>
          <div className="bg-[#F5EFE6] border border-[#B89154]/20 rounded-sm p-4">
            <Overline>{t("swot.opportunities")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{sw.opportunities || "—"}</p>
          </div>
          <div className="bg-[#F1EBE3] border border-[#5C5B5A]/15 rounded-sm p-4">
            <Overline>{t("swot.threats")}</Overline>
            <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{sw.threats || "—"}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (moduleKey === "operations") {
    return (
      <Card>
        <Overline>{t("module.operations")}</Overline>
        <H2>{t("module.operations")}</H2>
        <H3>{t("ops.description")}</H3><P>{d.description}</P>
        <H3>{t("ops.services")}</H3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-[#8A8886] border-b border-[#E8E6E1]">
              <tr>
                <th className="text-left py-2">{t("ops.service_name")}</th>
                <th className="text-left py-2">{t("ops.service_desc")}</th>
                <th className="text-right py-2">{t("ops.price")}</th>
                <th className="text-right py-2">{t("ops.margin")}</th>
                <th className="text-left py-2">{t("ops.duration")}</th>
              </tr>
            </thead>
            <tbody>
              {(d.services || []).map((s, i) => (
                <tr key={i} className="border-b border-[#F0EFE9]">
                  <td className="py-2 text-[#1A1A1A]">{s.name}</td>
                  <td className="py-2 text-[#5C5B5A] text-xs">{s.description}</td>
                  <td className="py-2 text-right font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(s.price)}</td>
                  <td className="py-2 text-right font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{s.margin}%</td>
                  <td className="py-2 text-[#5C5B5A]">{s.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <H3>{t("ops.processes")}</H3><P>{d.key_processes}</P>
        <H3>{t("ops.suppliers")}</H3><P>{d.suppliers_partners}</P>
      </Card>
    );
  }

  if (moduleKey === "hr") {
    return (
      <Card>
        <Overline>{t("module.hr")}</Overline>
        <H2>{t("module.hr")}</H2>
        <H3>{t("hr.needs")}</H3><P>{d.needs_description}</P>
        <H3>{t("hr.roles")}</H3>
        <div className="space-y-3">
          {(d.roles || []).map((r, i) => (
            <div key={i} className="border border-[#E8E6E1] rounded-sm p-4 bg-[#FBFAF7]">
              <div className="flex justify-between items-baseline gap-4">
                <div className="text-base text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>{r.title}</div>
                <div className="text-xs text-[#8A8886]">×{r.headcount || 1}</div>
              </div>
              <p className="text-xs text-[#5C5B5A] mt-2">{r.responsibilities}</p>
              <div className="text-xs mt-2 font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{r.salary_range}</div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (moduleKey === "calendar") {
    return (
      <Card>
        <Overline>{t("module.calendar")}</Overline>
        <H2>{t("module.calendar")}</H2>
        <H3>{t("calendar.annual")}</H3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(d.annual_milestones || []).map((m, i) => (
            <div key={i} className="border border-[#E8E6E1] rounded-sm p-4 bg-[#FBFAF7]">
              <Overline>{m.quarter}</Overline>
              <div className="mt-2 text-sm font-medium text-[#1A1A1A]">{m.title}</div>
              <p className="text-xs text-[#5C5B5A] mt-2">{m.description}</p>
            </div>
          ))}
        </div>
        <H3>{t("calendar.five_year")}</H3>
        <div className="space-y-2">
          {(d.five_year_plan || []).map((y, i) => (
            <div key={i} className="border-b border-[#F0EFE9] py-3">
              <Overline>{y.year}</Overline>
              <p className="mt-1 text-sm text-[#1A1A1A]">{y.goals}</p>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (moduleKey === "marketing") {
    return (
      <Card>
        <Overline>{t("module.marketing")}</Overline>
        <H2>{t("module.marketing")}</H2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("marketing.target_cpl")}</Overline>
            <div className="mt-2 text-3xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(d.target_cpl)}</div>
          </div>
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("marketing.target_cac")}</Overline>
            <div className="mt-2 text-3xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(d.target_cac)}</div>
          </div>
        </div>
        <H3>{t("marketing.b2b")}</H3>
        <div className="space-y-2">
          {(d.b2b_actions || []).map((a, i) => (
            <div key={i} className="flex justify-between gap-4 border-b border-[#F0EFE9] py-2 text-sm">
              <div><span className="text-[#1A1A1A] font-medium">{a.channel}</span><div className="text-xs text-[#5C5B5A]">{a.description}</div></div>
              <div className="font-mono text-[#2563EB] shrink-0" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(a.budget)}</div>
            </div>
          ))}
        </div>
        <H3>{t("marketing.b2c")}</H3>
        <div className="space-y-2">
          {(d.b2c_actions || []).map((a, i) => (
            <div key={i} className="flex justify-between gap-4 border-b border-[#F0EFE9] py-2 text-sm">
              <div><span className="text-[#1A1A1A] font-medium">{a.channel}</span><div className="text-xs text-[#5C5B5A]">{a.description}</div></div>
              <div className="font-mono text-[#2563EB] shrink-0" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(a.budget)}</div>
            </div>
          ))}
        </div>
        <H3>{t("marketing.digital")}</H3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-[#8A8886] border-b border-[#E8E6E1]">
            <tr>
              <th className="text-left py-2">{t("marketing.platform")}</th>
              <th className="text-right py-2">{t("marketing.budget")}</th>
              <th className="text-right py-2">{t("marketing.leads")}</th>
              <th className="text-right py-2">{t("marketing.conversions")}</th>
            </tr>
          </thead>
          <tbody>
            {(d.digital_campaigns || []).map((c, i) => (
              <tr key={i} className="border-b border-[#F0EFE9]">
                <td className="py-2 text-[#1A1A1A]">{c.platform}</td>
                <td className="py-2 text-right font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(c.budget)}</td>
                <td className="py-2 text-right font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{c.leads}</td>
                <td className="py-2 text-right font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{c.conversions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    );
  }

  if (moduleKey === "budget") {
    const monthlyRevenue = (d.revenue_streams || []).reduce((s, r) => s + (parseFloat(r.monthly_amount) || 0), 0);
    const monthlyFixed = (d.fixed_monthly || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const monthlyVariable = (d.variable_monthly || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const totalStartup = (d.startup_costs || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const y1Revenue = monthlyRevenue * 12;
    const y1Expenses = (monthlyFixed + monthlyVariable) * 12;
    const y1Tax = Math.max(0, (y1Revenue - y1Expenses) * ((d.tax_rate || 25) / 100));
    const y1Net = y1Revenue - y1Expenses - y1Tax;

    return (
      <Card>
        <Overline>{t("module.budget")}</Overline>
        <H2>{t("module.budget")}</H2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("fin.initial_capital")}</Overline>
            <div className="mt-2 text-2xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(d.initial_capital)}</div>
          </div>
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("budget.startup")}</Overline>
            <div className="mt-2 text-2xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(totalStartup)}</div>
          </div>
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("fin.revenue")} Y1</Overline>
            <div className="mt-2 text-2xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(y1Revenue)}</div>
          </div>
          <div className="border border-[#E8E6E1] rounded-sm p-4">
            <Overline>{t("fin.net_income")} Y1</Overline>
            <div className={`mt-2 text-2xl font-mono ${y1Net >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`} style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(y1Net)}</div>
          </div>
        </div>
        <H3>{t("budget.startup")}</H3>
        {(d.startup_costs || []).map((c, i) => (
          <div key={i} className="flex justify-between border-b border-[#F0EFE9] py-2 text-sm">
            <span>{c.item}</span>
            <span className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(c.amount)}</span>
          </div>
        ))}
        <H3>{t("budget.fixed")}</H3>
        {(d.fixed_monthly || []).map((c, i) => (
          <div key={i} className="flex justify-between border-b border-[#F0EFE9] py-2 text-sm">
            <span>{c.item}</span>
            <span className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(c.amount)}/mo</span>
          </div>
        ))}
        <H3>{t("budget.revenue")}</H3>
        {(d.revenue_streams || []).map((c, i) => (
          <div key={i} className="flex justify-between border-b border-[#F0EFE9] py-2 text-sm">
            <span>{c.name}</span>
            <span className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{fmtEur(c.monthly_amount)}/mo</span>
          </div>
        ))}
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
          <div className="border border-[#E8E6E1] rounded-sm p-3">
            <Overline>{t("fin.tax_rate")}</Overline>
            <div className="mt-1 font-mono">{d.tax_rate || 25}%</div>
          </div>
          <div className="border border-[#E8E6E1] rounded-sm p-3">
            <Overline>{t("fin.yearly_rev_growth")}</Overline>
            <div className="mt-1 font-mono">{d.yearly_revenue_growth || 0}%</div>
          </div>
        </div>
      </Card>
    );
  }

  if (moduleKey === "risks") {
    return (
      <Card>
        <Overline>{t("module.risks")}</Overline>
        <H2>{t("risk.title")}</H2>
        <div className="space-y-3 mt-4">
          {(d.items || []).map((r, i) => (
            <div key={i} className="border border-[#E8E6E1] rounded-sm p-4 bg-[#FBFAF7]">
              <div className="flex justify-between items-baseline gap-4">
                <div className="text-base text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>{r.title}</div>
                <div className="flex gap-2 shrink-0">
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-[#E8E6E1] rounded-sm">{r.probability}</span>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-[#E8E6E1] rounded-sm">{r.impact}</span>
                </div>
              </div>
              <p className="text-xs text-[#5C5B5A] mt-2">{r.description}</p>
              <div className="text-xs mt-2"><span className="text-[#8A8886]">{t("risk.mitigation")}: </span>{r.mitigation}</div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Overline>{moduleKey}</Overline>
      <pre className="mt-3 text-xs text-[#5C5B5A] overflow-auto">{JSON.stringify(d, null, 2)}</pre>
    </Card>
  );
}
